import Konva from "konva";
import React from "react";
import { Stage } from "react-konva";
import { ZoomControls } from "../../ZoomControls";
import { useMapCanvas, useMapCanvasStage, useMapCanvasStagePosition } from "../contexts/MapCanvasContext";
import { getWindowKonvaObject } from "../helpers/konva/getWindowKonvaObject";
import { useZoomStageToScale } from "../helpers/useZoomStageToScale";
import { useGetViewShapesByType } from "../hooks/useGetViewShapesByType";
import { useMapViewHandlers } from "../hooks/useMapViewHandlers";
import { CanvasMiniMap } from "./CanvasMiniMap";
import { MapCanvasViewLayers } from "./MapCanvasViewLayers";

interface CanvasProps {
  showOutside: boolean;
  miniMapWidth?: number;
  miniMapHeight?: number;
}

const classes = {
  Wrapper: "relative w-full h-full",
  ZoomControlsWrapper:
    "flex rounded-md items-center absolute top-10 right-10 bg-background-primary shadow-sm"
};

export const MapCanvas: React.FC<CanvasProps> = ({
  showOutside = true,
  miniMapWidth,
  miniMapHeight
}) => {
  const stageRef = React.useRef<Konva.Stage>(null);
  const [sizeInitialized, setSizeInitialized] = React.useState(false);

  const {
    width,
    height,
    editingZoneWidth,
    editingZoneHeight
  } = useMapCanvas();
  const { stagePosition } = useMapCanvasStagePosition();
  const { centerStage } = useMapCanvasStage();
  const getShapes = useGetViewShapesByType();
  const { cursor, ...handlers } = useMapViewHandlers({ stageRef });

  React.useEffect(() => {
    if (
      !sizeInitialized &&
      stageRef.current &&
      width > 0 &&
      height > 0 &&
      editingZoneWidth > 0 &&
      editingZoneHeight > 0
    ) {
      centerStage();
      setSizeInitialized(true);

    }
  }, [width, height, stageRef.current]);

  const handleZoom = useZoomStageToScale();

  const handleZoomIn = () => {
    const newScale = Math.min(stagePosition.scale * 1.2, 5);
    handleZoom(getWindowKonvaObject().stages[0], newScale, true);
  };

  const handleZoomOut = () => {
    const newScale = Math.max(stagePosition.scale / 1.2, 0.1);
    handleZoom(getWindowKonvaObject().stages[0], newScale, true);
  };

  return (
    <div className={classes.Wrapper}>
      <Stage
        style={{ cursor }}
        ref={stageRef}
        width={width}
        height={height}
        pixelRatio={1}
        scaleX={stagePosition.scale}
        scaleY={stagePosition.scale}
        x={stagePosition.x}
        y={stagePosition.y}
        draggable={true}
        dragBoundFunc={(pos) => pos}
        {...handlers}
      >
        <MapCanvasViewLayers showOutside={showOutside} getShapes={getShapes} />
      </Stage>

      <CanvasMiniMap
        stageRef={stageRef}
        width={miniMapWidth}
        height={miniMapHeight}
      />

      <div className={classes.ZoomControlsWrapper}>
        <ZoomControls
          scale={stagePosition.scale}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onCenter={centerStage}
        />
      </div>
    </div>
  );
};
